"use client"

import React, { useState, useEffect } from "react"
import { Box, Typography, Button, Paper, Grid, Alert, Chip, CircularProgress } from "@mui/material"
import { ArrowBack, LocalHospital, PersonSearch, LocationOn, SearchOff, Tune, MyLocation } from "@mui/icons-material"
import FilterSidebar from "./FilterSidebar"
import ProviderCard from "./ProviderCard"
import ProvidersMap from "./ProvidersMap"
import { searchProvidersAdaptive } from "./providers-api"

const SearchResults = ({ searchCriteria, currentFilters, onBack, onProviderSelect, onFiltersUpdate, isPublicRoute = false }) => {
  const [providers, setProviders] = useState([])
  const [loading, setLoading] = useState(true)
  const [totalCount, setTotalCount] = useState(0)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [initialLoad, setInitialLoad] = useState(true)

  // Use currentFilters if available, otherwise initialize from search criteria
  const [filters, setFilters] = useState(() => 
    currentFilters || {
      location: searchCriteria.location || null,
      provider: searchCriteria.provider,
      specialty: searchCriteria.specialty,
      distance: 5,
    }
  )
  
  // Update filters if currentFilters prop changes (when coming back from details)
  useEffect(() => {
    if (currentFilters) {
      setFilters(currentFilters)
    }
  }, [currentFilters])

  // Reload providers when filters change
  useEffect(() => {
    loadProviders(1, true)
  }, [])

  const loadProviders = async (pageToLoad = 1, replace = false) => {
    setLoading(true)
    if (replace) {
      setInitialLoad(true)
    }
    
    try {
      // Provider is now a string, not an object
      const providerName = typeof filters.provider === 'string' ? filters.provider : filters.provider?.name
      
      const response = await searchProvidersAdaptive({
        organizationId: searchCriteria.organization?.id,
        organizationName: searchCriteria.organizationName || "",
        planId: searchCriteria.plan?.id,
        location: filters.location,
        providerId: null, // No longer using provider ID
        providerName: providerName || "", // Use the text input directly
        specialtyId: filters.specialty?.id,
        distance: filters.distance,
        page: pageToLoad,
        limit: 10,
      }, isPublicRoute)

      if (replace) {
        setProviders(response.providers || [])
      } else {
        setProviders((prev) => [...prev, ...(response.providers || [])])
      }

      setTotalCount(response.totalCount || 0)
      setHasMore(response.hasMore)
    } catch (error) {
      console.error("Error loading providers:", error)
      setProviders([])
      setTotalCount(0)
      setHasMore(false)
    } finally {
      setLoading(false)
      setInitialLoad(false)
    }
  }

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters)
    // Notify parent component about filter changes
    onFiltersUpdate(newFilters)
  }

  const handleLoadMore = () => {
    const nextPage = page + 1
    setPage(nextPage)
    loadProviders(nextPage, false)
  }

  const handleProviderSelect = (provider) => {
    // Pass the complete provider data along with searchCriteria and current filters
    onProviderSelect(provider, searchCriteria, filters)
  }

  const getMapLocations = () => {
    return providers
      .map((provider) => ({
        id: provider.id,
        name: provider.name,
        lat: provider.locations?.[0]?.coordinates?.lat,
        lng: provider.locations?.[0]?.coordinates?.lng,
        address: provider.locations?.[0]?.address,
        phone: provider.locations?.[0]?.phone,
      }))
      .filter((loc) => loc.lat && loc.lng)
  }

  const getMapCenter = () => {
    return filters.location?.coordinates || { lat: 37.7749, lng: -122.4194 }
  }

  const handleMapLocationClick = (location) => {
    const provider = providers.find((p) => p.id === location.id)
    if (provider) handleProviderSelect(provider)
  }

  // Get the current search criteria display text for loading message
  const getLoadingMessage = () => {
    const distance = filters.distance || 5
    const location = filters.location?.placeName || 'your location'
    return `Loading providers within ${distance} miles of ${location} based on your search criteria...`
  }

  // Enhanced Loading Component
  const LoadingComponent = () => (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f5f5f5" }}>
      <Paper 
        elevation={3} 
        sx={{ 
          height: "400px",
          width: "90%",
          borderRadius: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          bgcolor: "white",
          p: 4,
          mr: 2
        }}
      >
        {/* Loading Icons Animation */}
        <Box sx={{ 
          display: "flex", 
          justifyContent: "center", 
          alignItems: "center",
          mb: 4,
          gap: 2
        }}>
          <Box sx={{ 
            animation: "pulse 2s infinite",
            "@keyframes pulse": {
              "0%": { opacity: 0.3 },
              "50%": { opacity: 1 },
              "100%": { opacity: 0.3 }
            }
          }}>
            <LocalHospital sx={{ fontSize: 40, color: "#036fcb" }} />
          </Box>
          <Box sx={{ 
            animation: "pulse 2s infinite 0.5s",
            "@keyframes pulse": {
              "0%": { opacity: 0.3 },
              "50%": { opacity: 1 },
              "100%": { opacity: 0.3 }
            }
          }}>
            <PersonSearch sx={{ fontSize: 40, color: "#036fcb" }} />
          </Box>
          <Box sx={{ 
            animation: "pulse 2s infinite 1s",
            "@keyframes pulse": {
              "0%": { opacity: 0.3 },
              "50%": { opacity: 1 },
              "100%": { opacity: 0.3 }
            }
          }}>
            <LocationOn sx={{ fontSize: 40, color: "#036fcb" }} />
          </Box>
        </Box>

        {/* Loading Text */}
        <Typography 
          variant="h6" 
          sx={{ 
            color: "#333", 
            fontWeight: 600,
            mb: 2
          }}
        >
          Finding your healthcare providers
        </Typography>
        
        <Typography 
          sx={{ 
            color: "#666", 
            fontSize: "0.875rem",
            lineHeight: 1.5,
            maxWidth: 400,
            mx: "auto"
          }}
        >
          {getLoadingMessage()}
        </Typography>

        {/* Progress Dots */}
        <Box sx={{ 
          display: "flex", 
          justifyContent: "center", 
          gap: 0.5,
          mt: 4
        }}>
          {[0, 1, 2].map((index) => (
            <Box
              key={index}
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: "#036fcb",
                animation: `bounce 1.4s ease-in-out infinite both`,
                animationDelay: `${index * 0.2}s`,
                "@keyframes bounce": {
                  "0%, 80%, 100%": {
                    transform: "scale(0)",
                  },
                  "40%": {
                    transform: "scale(1)",
                  },
                },
              }}
            />
          ))}
        </Box>
      </Paper>
    </Box>
  )

  // Enhanced No Providers Found Component
  const NoProvidersFoundComponent = ({ filters }) => {
    const getSearchDetails = () => {
      const distance = filters.distance || 5
      const location = filters.location?.placeName || 'your location'
      return `within ${distance} miles of ${location}`
    }

    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
        <Paper 
          elevation={3} 
          sx={{ 
            width: "100%",
            maxWidth: "500px",
            borderRadius: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            bgcolor: "white",
            p: 4,
            border: "1px solid #f0f0f0"
          }}
        >
          {/* No Results Icons */}
          <Box sx={{ 
            display: "flex", 
            justifyContent: "center", 
            alignItems: "center",
            mb: 3,
            gap: 1.5
          }}>
            <Box sx={{ 
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}>
              <SearchOff sx={{ 
                fontSize: 48, 
                color: "#9e9e9e",
                opacity: 0.7
              }} />
            </Box>
            <Box sx={{ 
              animation: "fadeInOut 3s infinite",
              "@keyframes fadeInOut": {
                "0%": { opacity: 0.3 },
                "50%": { opacity: 0.8 },
                "100%": { opacity: 0.3 }
              }
            }}>
              <Tune sx={{ fontSize: 32, color: "#bdbdbd" }} />
            </Box>
          </Box>

          {/* Main Message */}
          <Typography 
            variant="h6" 
            sx={{ 
              color: "#424242", 
              fontWeight: 600,
              mb: 2,
              fontSize: "1.1rem"
            }}
          >
            No providers found for your search criteria
          </Typography>
          
          {/* Search Details */}
          <Typography 
            sx={{ 
              color: "#757575", 
              fontSize: "0.875rem",
              mb: 3,
              fontStyle: "italic"
            }}
          >
            Searched {getSearchDetails()}
          </Typography>

          {/* Suggestions */}
          <Box sx={{ 
            bgcolor: "#f8f9fa", 
            borderRadius: 2, 
            p: 2.5,
            width: "100%",
            border: "1px solid #e9ecef"
          }}>
            <Typography 
              sx={{ 
                color: "#495057", 
                fontSize: "0.875rem",
                lineHeight: 1.6,
                mb: 2,
                fontWeight: 500
              }}
            >
              Try these suggestions:
            </Typography>
            
            <Box sx={{ textAlign: "left" }}>
              <Typography 
                sx={{ 
                  color: "#6c757d", 
                  fontSize: "0.8rem",
                  lineHeight: 1.5,
                  mb: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1
                }}
              >
                <MyLocation sx={{ fontSize: 16, color: "#6c757d" }} />
                Expand your search distance
              </Typography>
              <Typography 
                sx={{ 
                  color: "#6c757d", 
                  fontSize: "0.8rem",
                  lineHeight: 1.5,
                  mb: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1
                }}
              >
                <Tune sx={{ fontSize: 16, color: "#6c757d" }} />
                Remove or adjust filters
              </Typography>
              <Typography 
                sx={{ 
                  color: "#6c757d", 
                  fontSize: "0.8rem",
                  lineHeight: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 1
                }}
              >
                <LocationOn sx={{ fontSize: 16, color: "#6c757d" }} />
                Try a different location
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    )
  }

  // Show loading overlay for initial load or when updating results
  if (loading && (initialLoad || page === 1)) {
    return <LoadingComponent />
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f5f5f5" }}>
      <Box sx={{borderBottom: "1px solid #e0e0e0", p: 1.5,pl: 0, display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "left" }}>
        {/* Payer and Plan chips hidden as requested */}
        {/* {searchCriteria.organization?.name && (
          <Chip
            label={`Payer: ${searchCriteria.organization?.name}`}
            color="primary"
            variant="outlined"
            size="small"
            sx={{ 
              fontWeight: 500, 
              fontSize: "0.75rem",
              height: "24px",
              "& .MuiChip-label": { px: 1 }
            }}
          />
        )} */}
        {/* {searchCriteria.plan?.name && (
          <Chip
            label={`Plan: ${searchCriteria.plan?.name}`}
            color="primary"
            variant="outlined"
            size="small"
            sx={{ 
              fontWeight: 500, 
              fontSize: "0.75rem",
              height: "24px",
              "& .MuiChip-label": { px: 1 }
            }}
          />
        )} */}
        {filters.provider && typeof filters.provider === 'string' && filters.provider.trim() && (
          <Chip
            label={`Provider: ${filters.provider}`}
            color="primary"
            variant="outlined"
            size="small"
            sx={{ 
              fontWeight: 500, 
              fontSize: "0.75rem",
              height: "24px",
              "& .MuiChip-label": { px: 1 }
            }}
          />
        )}
        {filters.specialty?.name && (
          <Chip
            label={`Specialty: ${filters.specialty?.name}`}
            color="primary"
            variant="outlined"
            size="small"
            sx={{ 
              fontWeight: 500, 
              fontSize: "0.75rem",
              height: "24px",
              "& .MuiChip-label": { px: 1 }
            }}
          />
        )}
        {filters.location?.placeName && (
          <Chip
            label={`Location: ${filters.location?.placeName}`}
            color="primary"
            variant="outlined"
            size="small"
            sx={{ 
              fontWeight: 500, 
              fontSize: "0.75rem",
              height: "24px",
              "& .MuiChip-label": { px: 1 }
            }}
          />
        )}
      </Box>

      <Box sx={{ mx: "auto", p: 2,pl: 0 }}>
        {/* Filters on top */}
        <Box sx={{ mb: 2 }}>
          <FilterSidebar
            searchCriteria={searchCriteria}
            filters={filters}
            onFilterChange={handleFilterChange}
            loading={loading}
            onUpdate={() => {
                setPage(1)
                loadProviders(1, true)
            }}
            isPublicRoute={isPublicRoute}
          />
        </Box>

        <Grid container spacing={2}>
          {/* Provider List */}
          <Grid item xs={7} lg={7}>
            <Box sx={{ height: "80vh", overflow: "auto", pr: 1 }}>
              {loading && providers.length === 0 ? (
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <Typography>Loading providers...</Typography>
                </Box>
              ) : providers.length === 0 ? (
                <NoProvidersFoundComponent filters={filters} />
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {providers.map((provider) => (
                    <ProviderCard
                      key={provider.id}
                      provider={provider}
                      userLocation={filters.location?.coordinates}
                      onViewDetails={() => handleProviderSelect(provider)}
                    />
                  ))}

                  {/* Load More */}
                  {hasMore && (
                    <Button
                      variant="outlined"
                      onClick={handleLoadMore}
                      disabled={loading}
                      sx={{ mt: 2 }}
                    >
                      {loading ? "Loading..." : "Load more"}
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </Grid>

          {/* Map */}
          <Grid item xs={5} lg={5}>
            <Paper sx={{ height: "80vh", borderRadius: 2, overflow: "hidden" }}>
              <ProvidersMap
                locations={getMapLocations()}
                center={getMapCenter()}
                onLocationClick={handleMapLocationClick}
              />
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}

export default SearchResults